import type { ReactNode } from "react";
import { API_URL, ApiRequestError } from "../api";

/** A quiet mark for a full-page empty state. Not for a blank cell in a table. */
export function EmptyMark({ children }: { children: ReactNode }) {
  return (
    <span className="empty-mark" aria-hidden="true">
      {children}
    </span>
  );
}

const mark = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

export function EmptySessionsMark() {
  return (
    <EmptyMark>
      <svg {...mark}><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 21h8M10 8.5l5 3-5 3Z" /></svg>
    </EmptyMark>
  );
}

export function EmptyLiveMark() {
  return (
    <EmptyMark>
      <svg {...mark}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></svg>
    </EmptyMark>
  );
}

export function EmptySearchMark() {
  return (
    <EmptyMark>
      <svg {...mark}><circle cx="11" cy="11" r="6" /><path d="m20 20-3.5-3.5" /></svg>
    </EmptyMark>
  );
}

export function EmptyBellMark() {
  return (
    <EmptyMark>
      <svg {...mark}><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15Z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>
    </EmptyMark>
  );
}

export function EmptyPlugMark() {
  return (
    <EmptyMark>
      <svg {...mark}><path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0V8Z" /><path d="M12 17v4" /></svg>
    </EmptyMark>
  );
}

export function EmptyListMark() {
  return (
    <EmptyMark>
      <svg {...mark}><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></svg>
    </EmptyMark>
  );
}

/** A quiet full-card empty state: a mark, one line, and at most one action. */
export function EmptyState({ mark, title, hint, action }: { mark: ReactNode; title: ReactNode; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty" role="status">
      {mark}
      <h3>{title}</h3>
      {hint && <p>{hint}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const e = error as Partial<ApiRequestError> & { message?: string };
  const isNetwork = e instanceof ApiRequestError && e.code === "network";
  return (
    <div className="error-box" role="alert">
      <strong>{isNetwork ? "API unreachable" : "Something went wrong"}</strong>
      <div style={{ marginTop: 6 }}>{e?.message ?? String(error)}</div>
      {isNetwork && (
        <div style={{ marginTop: 8, color: "var(--fg-muted)" }}>
          Expected the API at <code>{API_URL}</code>. Start the local stack with <code>make up</code> and the api service, or set <code>VITE_API_URL</code>.
        </div>
      )}
      {retry && (
        <button className="btn" style={{ marginTop: 10 }} onClick={retry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function EmptySessions() {
  return (
    <div className="empty">
      <EmptySessionsMark />
      <h3>No sessions yet</h3>
      <p>Add Backstory to a page and use that page. Sessions appear here within seconds.</p>
      <div className="empty-action">
        <a href="https://docs.backstory.io/getting-started/quickstart/" target="_blank" rel="noreferrer">
          See how to install
        </a>
      </div>
    </div>
  );
}

export function Skeleton({ lines = 6 }: { lines?: number }) {
  return (
    <div style={{ display: "grid", gap: 8, padding: 16 }} aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className="skeleton" style={{ height: 18, width: `${60 + ((i * 13) % 40)}%` }}>
          loading
        </span>
      ))}
    </div>
  );
}
